import { WaqLink } from "../../../ui/waq/WaqLink";
import { useId, useState } from "react";
import { GlowBlob } from "../../../ui/waq/GlowBlob";
import { SectionHairline } from "../../../ui/waq/SectionHairline";
import {
  DEFAULT_SCORES,
  bandFor,
  maturitySection,
  questions,
} from "../../../../content/waq/maturity";

/**
 * design/Homepage.dc.html lines 347-392.
 *
 * Scoring is client-side and instant, exactly as the reference: six 1-5 scores
 * summed to 6-30, mapped to a band. Output is a band, never a per-question
 * score.
 *
 * The reference's range inputs have no labels and no hint association. Here each
 * slider is a real <label>, its hint is wired via aria-describedby, and the
 * results header is an aria-live region so the band is announced as it changes.
 */
export function MaturityCheck() {
  const [scores, setScores] = useState(DEFAULT_SCORES);
  const total = scores.reduce((a, b) => a + b, 0);
  const result = bandFor(total);
  const baseId = useId();

  const setScore = (i: number, value: number) =>
    setScores((prev) => prev.map((s, idx) => (idx === i ? value : s)));

  return (
    <section id="assessment" className="bg-field" aria-labelledby="maturity-heading">
      <SectionHairline />

      <div className="mx-auto grid max-w-container grid-cols-[repeat(auto-fit,minmax(min(400px,100%),1fr))] items-start gap-2col px-section-x py-light-2col-y">
        <div>
          <p className="mb-3.5 text-eyebrow font-extrabold uppercase text-blue">
            {maturitySection.eyebrow}
          </p>
          <h2 id="maturity-heading" className="mb-4 text-h2-light font-extrabold">
            {maturitySection.heading}
          </h2>
          <p className="mb-7 max-w-[520px] text-body-lg text-soft">
            {maturitySection.lead}
          </p>

          <div className="relative overflow-hidden rounded-panel bg-navy p-8 text-surface shadow-dark-panel">
            <GlowBlob
              position={{ top: -160, right: -120 }}
              size={520}
              alpha={0.34}
              falloff="62%"
              animated
              duration="slow"
            />

            <div
              aria-live="polite"
              className="relative flex items-end gap-4 border-b border-white/[.18] pb-5"
            >
              <span className="text-total font-extrabold tabular-nums text-yellow">
                {total}
              </span>
              <span className="pb-1.5">
                <span className="block text-sm-13 font-bold text-white/60">
                  {maturitySection.outOf}
                </span>
                <span className="block text-band font-extrabold">{result.band}</span>
              </span>
            </div>

            <p className="relative my-[18px] mb-5 text-band-note text-white/80">
              {result.note}
            </p>

            <p className="relative mb-3 text-label-mid font-extrabold uppercase text-blue">
              {maturitySection.movesLabel}
            </p>

            <ul className="relative grid list-none gap-2.5">
              {result.moves.map((move) => (
                <li
                  key={move}
                  className="flex items-start gap-3 text-move font-semibold"
                >
                  <span
                    aria-hidden="true"
                    className="flex h-5 w-5 flex-none items-center justify-center rounded-md bg-yellow text-[11px] font-extrabold text-navy"
                  >
                    ✓
                  </span>
                  <span>{move}</span>
                </li>
              ))}
            </ul>

            <WaqLink
              href={maturitySection.cta.href}
              className="relative mt-6 flex items-center justify-center gap-2.5 rounded-btn bg-yellow p-[15px] text-[15px] font-bold text-navy"
            >
              {maturitySection.cta.label} <span aria-hidden="true">→</span>
            </WaqLink>
          </div>
        </div>

        <div className="grid gap-2.5">
          {/* One legend for all six sliders. The reference repeats the scale
              under every question, which is six identical rows of the same two
              words — the single largest source of height in this column. The
              range inputs still announce their own 1-to-5 value, so nothing is
              lost for screen readers. */}
          <div className="flex justify-between px-5 text-scale-label font-bold uppercase text-soft">
            <span>{maturitySection.scaleLow}</span>
            <span>{maturitySection.scaleHigh}</span>
          </div>

          {questions.map((question, i) => {
            const inputId = `${baseId}-q${i}`;
            const hintId = `${baseId}-h${i}`;
            return (
              <div
                key={question.label}
                className="rounded-card border border-hair bg-surface px-5 py-4"
              >
                <div className="mb-1.5 flex items-start justify-between gap-3">
                  <label htmlFor={inputId} className="text-q-label font-extrabold">
                    {question.label}
                  </label>
                  <span
                    aria-hidden="true"
                    className="flex h-7 w-7 flex-none items-center justify-center rounded-item bg-navy text-check-item font-extrabold tabular-nums text-yellow"
                  >
                    {scores[i]}
                  </span>
                </div>

                <p id={hintId} className="mb-3 text-hint text-soft">
                  {question.hint}
                </p>

                <input
                  id={inputId}
                  type="range"
                  min={1}
                  max={5}
                  step={1}
                  value={scores[i]}
                  aria-describedby={hintId}
                  onChange={(e) => setScore(i, Number(e.target.value))}
                  className="block w-full cursor-pointer accent-blue"
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
